import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Surface } from '@heroui/react';
import { captureAppError, type AppErrorKind } from '@/lib/sentry';
import { useTranslation } from 'react-i18next';

/**
 * Top-level Error Boundary so a runtime crash in any sub-tree shows
 * a recovery affordance instead of a blank screen. Two specific cases
 * this protects against:
 *
 *   1. Stale lazy-loaded chunks after a deploy. `lazyWithReload` catches
 *      most of these and refreshes silently; this boundary catches the
 *      handful that slip through (e.g. a second deploy in the same tab,
 *      or any other lazy import we forgot to wrap).
 *   2. Unexpected runtime errors anywhere in the tree. A render that
 *      throws would otherwise unmount React's root and leave the user
 *      staring at a cream void with no idea what happened.
 *
 * UI: small centred card with a friendly recovery message + a
 * "Перезагрузить" button. The raw error is logged to the console (and
 * Sentry) in componentDidCatch — never shown to the user, so internal
 * details don't leak into the UI. Keeps the dot-matrix BG (body::before)
 * so the page doesn't look TOTALLY broken, just "something went wrong here".
 */
interface State {
  err: Error | null;
  incidentId: string;
  copied: boolean;
}

function classifyError(err: Error): AppErrorKind {
  if (
    /Failed to fetch dynamically imported module/i.test(err.message) ||
    /Importing a module script failed/i.test(err.message) ||
    /Loading chunk \d+ failed/i.test(err.message)
  ) return 'chunk';
  if (/fetch|network|load failed|offline/i.test(err.message)) return 'network';
  return 'runtime';
}

function makeIncidentId(): string {
  return `BNK-${crypto.randomUUID().slice(0, 8).toUpperCase()}`;
}

export class AppErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { err: null, incidentId: makeIncidentId(), copied: false };

  static getDerivedStateFromError(err: Error): State {
    return { err, incidentId: makeIncidentId(), copied: false };
  }

  componentDidCatch(err: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[bunker] app boundary caught:', err, info.componentStack);
    captureAppError(err, {
      incidentId: this.state.incidentId,
      kind: classifyError(err),
      componentStack: info.componentStack,
    });
  }

  private copyDiagnostics = async (): Promise<void> => {
    const { err, incidentId } = this.state;
    if (!err) return;
    const details = [
      `Incident: ${incidentId}`,
      `Kind: ${classifyError(err)}`,
      `Route: ${window.location.href}`,
      `Online: ${navigator.onLine}`,
      `Desktop: ${window.bunkerDesktop?.isDesktop === true}`,
      `Time: ${new Date().toISOString()}`,
      `Error: ${err.name}: ${err.message}`,
    ].join('\n');
    await navigator.clipboard.writeText(details);
    this.setState({ copied: true });
  };

  render() {
    const { err } = this.state;
    if (!err) return this.props.children;

    return (
      <CrashScreen
        kind={classifyError(err)}
        incidentId={this.state.incidentId}
        copied={this.state.copied}
        onCopy={() => void this.copyDiagnostics()}
      />
    );
  }
}

/** The crash card. A function component so its text follows a language switch. */
function CrashScreen(props: { kind: ReturnType<typeof classifyError>; incidentId: string; copied: boolean; onCopy: () => void }) {
  const { t } = useTranslation();
  return (
      <div className="grid h-full w-full place-items-center bg-bg p-6 text-ink">
        <Surface variant="default" className="rounded-2xl max-w-md p-6 text-center">
          <div className="font-medium mb-3 !text-danger-ink">
            {t('appError.title')}
          </div>
          <p className="mb-4 text-sm text-ink-dim">
            {props.kind === 'chunk'
              ? t('appError.chunk')
              : props.kind === 'network'
                ? t('appError.network')
                : t('appError.generic')}
          </p>
          <div className="mb-4 rounded-lg bg-default/35 px-3 py-2 font-mono text-xs text-muted">
            {t('appError.code', { id: props.incidentId })}
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            <button
              type="button"
              onClick={props.onCopy}
              className="font-medium inline-flex items-center justify-center rounded-[8px] bg-default/40 px-4 py-2.5 text-sm text-ink transition-colors hover:bg-default/60"
            >
              {props.copied ? t('appError.copied') : t('appError.copyDiagnostics')}
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="font-medium inline-flex items-center justify-center rounded-[8px] bg-accent px-5 py-2.5 text-bg transition-colors hover:bg-accent-hover"
            >
              {t('appError.reload')}
            </button>
          </div>
        </Surface>
      </div>
  );
}
